'use client';

import React, { useState, useEffect, useRef, JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import { Text, Slider, getAccessibleFocusStyles } from '@videoblocks/design-system';
import ChevronDown from '@videoblocks/react-icons/ChevronDown';

export interface TextInputDropdownProps {
    value: number;
    minValue: number;
    maxValue: number;
    onChange: (value: number) => void;
    header: string;
    className?: string;
}
export const TextInputDropdown = ({
    value,
    minValue,
    maxValue,
    onChange,
    header,
    className,
}: TextInputDropdownProps): JSX.Element => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const formattedValue = `${(value / 100).toFixed(2)}x`;
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen]);

    return (
        <div className="relative w-full md:w-auto" ref={dropdownRef}>
            {/* Main Button */}
            <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={twMerge(
                    'flex w-full md:w-auto justify-center items-center gap-x-3 rounded-capsule border border-gray-500 px-3 py-3 bg-[#252c2d] cursor-pointer min-w-[124px] hover:bg-gray-700',
                    getAccessibleFocusStyles(),
                    className,
                )}
            >
                <Text fontWeight="regular">{formattedValue} Tempo</Text>
                <ChevronDown
                    className={`fill-current w-3 h-3 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900 rounded-lg shadow-xl z-10 border border-gray-700 w-[285px]">
                    <div className="px-4 py-3 border-b border-gray-700">
                        <h3 className="text-white font-semibold text-lg">{header}</h3>
                    </div>

                    <div className="py-3 px-4 mt-2 flex flex-col gap-2 items-end">
                        <Slider
                            value={[value]}
                            min={minValue}
                            max={maxValue}
                            onValueChange={(newValue) => {
                                onChange(newValue[0]);
                            }}
                            trackProps={{
                                className: 'bg-white',
                            }}
                            rangeProps={{
                                className: 'bg-blue-600',
                            }}
                        />
                        <Text fontSize="sm" fontWeight="regular">
                            {formattedValue}
                        </Text>
                    </div>
                </div>
            )}
        </div>
    );
};
